import { useEffect, useRef, useState } from 'react';

import { CLIENT_ID } from '~/components/shared/recommendations/stylitics/constants';
import type {
  StyliticsInsightsInstance,
  StyliticsInsightsPlacement,
} from '~/components/shared/recommendations/stylitics/types';
import { PageTypes } from '~/utilities/constants';
import { useCookieContext } from '~/utilities/context/dynamic/CookieContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import logger from '~/utilities/logger';

const SCRIPT_ID = 'stylitics-insights-module';
const LOADED_EVENT = 'stylitics-insights-loaded';

const placementMap = new Map<PageTypes, StyliticsInsightsPlacement | undefined>([
  [PageTypes.HOME, 'homepage'],
  [PageTypes.PRODUCT, 'pdp'],
  [PageTypes.OTHER, 'gallery'],
]);

const waitForScriptLoad = (): Promise<void> =>
  new Promise<void>(resolve => {
    if (window.__StyliticsInsightsLoaded) {
      resolve();
    } else {
      window.addEventListener(LOADED_EVENT, () => resolve(), { once: true });
    }
  });

export const useStyliticsInsights = (remoteId: string): StyliticsInsightsInstance | null => {
  const [insights, setInsights] = useState<StyliticsInsightsInstance | null>(null);
  const initRef = useRef(false);

  const { browserLocale, isProduction } = useAppContext();
  const { pageType } = useStaticContext();
  const { che: clientUserId } = useCookieContext();
  const {
    configuration: { defaultCurrency, styliticsOutfitsRecommendationsEnabled },
  } = useStaticContext();

  useEffect(() => {
    // Skip on SSR or if already initialized or if outfits recommendations are disabled
    if (
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      initRef.current ||
      !styliticsOutfitsRecommendationsEnabled
    ) {
      return undefined;
    }

    initRef.current = true;

    const loadScript = async () => {
      try {
        // Check if script is already loaded
        if (!document.getElementById(SCRIPT_ID)) {
          const script = document.createElement('script');

          script.id = SCRIPT_ID;
          script.type = 'module';
          script.textContent = `
            import StyliticsInsights from "https://web-assets.stylitics.com/insights-script/latest/stylitics-insights-script.js";

            // Store on window for access
            window.__StyliticsInsights = StyliticsInsights;
            window.__StyliticsInsightsLoaded = true;

            // Dispatch event to signal loading complete
            window.dispatchEvent(new Event('${LOADED_EVENT}'));
          `;
          document.head.appendChild(script);
        }

        // Wait for script to load
        await waitForScriptLoad();

        // Create insights instance
        if (window.__StyliticsInsights) {
          const placement = placementMap.get(pageType) || placementMap.get(PageTypes.OTHER);

          const insightsInstance = await window.__StyliticsInsights.create({
            client: CLIENT_ID,
            remoteId,
            placement,
            currency: defaultCurrency,
            locale: browserLocale,
            env: isProduction ? 'production' : 'staging',
            ...(clientUserId && { clientUserId }),
          });

          setInsights(insightsInstance);
        } else {
          throw new Error('StyliticsInsights not available');
        }
      } catch (err) {
        logger.error(err);
      }
    };

    loadScript();

    // Cleanup
    return () => {
      initRef.current = false;
    };
  }, [
    isProduction,
    pageType,
    remoteId,
    browserLocale,
    defaultCurrency,
    clientUserId,
    styliticsOutfitsRecommendationsEnabled,
  ]);

  return insights;
};
